/**
 * Browser-side image compressor for logo uploads.
 *
 * - Resizes large images to a max dimension (default 400px) while preserving aspect ratio
 * - Re-encodes as JPEG/PNG with configurable quality
 * - Returns a base64 data URL ready to store in DB
 *
 * Why: Original logos can be 2-5 MB. Storing them as-is bloats every API response
 * that embeds the logo (login, /me, tenant fetch). Compressing on upload prevents
 * the problem at the source.
 */

const DEFAULT_MAX_DIM = 400;       // px — logos rarely need to be larger
const DEFAULT_QUALITY = 0.85;      // JPEG quality 0-1
const DEFAULT_OUTPUT_FORMAT = 'image/jpeg';

/**
 * Compress an image File/Blob into a base64 data URL.
 * Transparent PNGs are kept as PNG to preserve transparency.
 *
 * @param {File|Blob} file - Browser File from <input type="file">
 * @param {Object} options - { maxDim, quality, outputFormat }
 * @returns {Promise<{ dataUrl: string, sizeKB: number, width: number, height: number }>}
 */
export const compressImage = (file, options = {}) => {
  const maxDim = options.maxDim || DEFAULT_MAX_DIM;
  const quality = options.quality || DEFAULT_QUALITY;
  const isPng = file.type === 'image/png';
  // Preserve PNG for transparency; otherwise encode as JPEG (much smaller).
  const outputFormat = options.outputFormat || (isPng ? 'image/png' : DEFAULT_OUTPUT_FORMAT);

  return new Promise((resolve, reject) => {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      reject(new Error('Invalid image file'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to load image'));
      img.onload = () => {
        let { width, height } = img;

        // Compute new dimensions preserving aspect ratio
        if (width > maxDim || height > maxDim) {
          if (width >= height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        // White background for JPEG (avoids black bleed where source is transparent)
        if (outputFormat === 'image/jpeg') {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL(outputFormat, quality);
        const sizeKB = Math.round((dataUrl.length * 0.75) / 1024); // base64 → bytes approx
        resolve({ dataUrl, sizeKB, width, height });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Convenience helper: returns just the data URL string after compression.
 * Falls back to original FileReader if compression fails for any reason.
 */
export const compressImageToDataUrl = async (file, options = {}) => {
  try {
    const result = await compressImage(file, options);
    return result.dataUrl;
  } catch (e) {
    console.warn('Image compression failed, falling back to original:', e);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
};

/**
 * Smart file reader for ANY upload (documents OR images).
 *
 * - Image files → automatically compressed (maxDim 1600px, quality 0.82 → ~150-400 KB)
 * - PDF / non-image files → passed through as-is (browser can't recompress PDFs)
 * - Returns { data, name, type, size } ready for backend storage.
 *
 * Use this for ALL document/photo upload flows (transcripts, student docs,
 * staff docs, ID cards, certificates, etc.) to avoid 5 MB base64 payloads.
 *
 * @param {File} file
 * @param {Object} [options] - { maxDim, quality, photoMaxDim, docMaxDim }
 *   photoMaxDim defaults to 1200 (full-size scans/photos)
 *   For ID card / passport photos, pass { photoMaxDim: 600 }
 * @returns {Promise<{ data: string, name: string, type: string, size: number, compressed: boolean }>}
 */
export const readFileSmart = async (file, options = {}) => {
  if (!file) throw new Error('No file');
  const maxDim = options.photoMaxDim || options.maxDim || 1200;
  const quality = options.quality || 0.82;

  // Images → compress
  if (file.type && file.type.startsWith('image/')) {
    try {
      const result = await compressImage(file, { maxDim, quality });
      // Approximate original byte size of base64 payload after compression
      const approxBytes = Math.round(result.dataUrl.length * 0.75);
      return {
        data: result.dataUrl,
        name: file.name,
        type: file.type.split('/')[1] || 'jpg',
        size: approxBytes,
        compressed: true,
      };
    } catch (e) {
      console.warn('Image compression failed, using raw:', e);
      // fall through to raw read
    }
  }

  // PDFs / non-images / fallback → read as-is
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = () => {
      resolve({
        data: reader.result,
        name: file.name,
        type: (file.type || '').split('/')[1] || 'bin',
        size: file.size,
        compressed: false,
      });
    };
    reader.readAsDataURL(file);
  });
};

